import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import PersonalInfoSection from './components/PersonalInfoSection';
import AddressSection from './components/AddressSection';
import InvoiceDeliverySection from './components/InvoiceDeliverySection';
import { getSession, upgradeAccount } from '../../utils/auth';

const PaymentInvoiceForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location?.search);
  const upgradeTarget = params?.get('upgrade');
  const isUpgradeFlow = !!upgradeTarget;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    streetAddress: '',
    postalCode: '',
    city: '',
    country: 'Norge',
    invoiceDelivery: 'email',
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const session = getSession();
    if (session) {
      setFormData(prev => ({
        ...prev,
        email: session?.email || '',
        fullName: session?.name || '',
        ...(session?.billingInfo || {}),
      }));
    }
  }, []);

  const validateField = (name, value) => {
    switch (name) {
      case 'fullName':
        return !value?.trim() ? 'Fullt navn er påkrevd' : '';
      case 'email':
        if (!value?.trim()) return 'E-postadresse er påkrevd';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(value)) return 'Ugyldig e-postadresse (f.eks. navn@eksempel.no)';
        return '';
      case 'phone':
        if (!value?.trim()) return 'Telefonnummer er påkrevd';
        if (!/^(\+47[\s]?)?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}$|^(\+47[\s]?)?[0-9]{8}$/?.test(value?.trim())) return 'Ugyldig telefonnummer (f.eks. +47 000 00 000)';
        return '';
      case 'streetAddress':
        return !value?.trim() ? 'Gateadresse er påkrevd' : '';
      case 'postalCode':
        return !value?.trim() ? 'Postnummer er påkrevd' : '';
      case 'city':
        return !value?.trim() ? 'By er påkrevd' : '';
      case 'country':
        return !value ? 'Land er påkrevd' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (touched?.[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validate = () => {
    const newErrors = {};
    ['fullName', 'email', 'phone', 'streetAddress', 'postalCode', 'city', 'country']?.forEach(field => {
      const err = validateField(field, formData?.[field]);
      if (err) newErrors[field] = err;
    });
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ fullName: true, email: true, phone: true, streetAddress: true, postalCode: true, city: true, country: true });
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1200));
    if (isUpgradeFlow) {
      upgradeAccount(formData);
    } else {
      const session = getSession();
      if (session) {
        const updatedSession = { ...session, billingInfo: formData };
        localStorage.setItem('veiassistent_session', JSON.stringify(updatedSession));
      }
    }
    setIsSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--color-background)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '260px', height: '260px', border: '2px solid rgba(0,79,152,0.07)', borderRadius: '2px', transform: 'rotate(15deg)', pointerEvents: 'none' }} />
        <Header />
        <main className="pt-[84px] px-4 sm:px-6 lg:px-8 pb-16 max-w-[720px] mx-auto">
          <div style={{ marginTop: '80px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', background: isUpgradeFlow ? 'rgba(245,158,11,0.1)' : 'rgba(34, 197, 94, 0.1)', border: `2px solid ${isUpgradeFlow ? 'rgba(245,158,11,0.4)' : 'rgba(34, 197, 94, 0.4)'}`, borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
              {isUpgradeFlow ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '32px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px', letterSpacing: '0.02em' }}>
              {isUpgradeFlow ? 'Velkommen til Premium!' : 'Fakturainformasjon lagret'}
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.6, marginBottom: '40px' }}>
              {isUpgradeFlow
                ? 'Din konto er oppgradert til Premium. Faktura sendes til din e-postadresse.' :'Din fakturainformasjon er oppdatert. Fremtidige fakturaer vil bli sendt i henhold til dine preferanser.'}
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate('/subscription-portal')}
                style={{ background: 'var(--color-primary)', border: '2px solid var(--color-primary)', borderRadius: 'var(--radius-sm)', padding: '12px 28px', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FFFFFF', cursor: 'pointer' }}
              >
                {isUpgradeFlow ? 'Se abonnement' : 'Ga til abonnement'}
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                style={{ background: 'transparent', border: '2px solid rgba(156, 163, 175, 0.3)', borderRadius: 'var(--radius-sm)', padding: '12px 28px', fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', cursor: 'pointer' }}
              >
                Dashboard
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)', position: 'relative', overflow: 'hidden' }}>
      {/* Architectural background accents */}
      <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', border: '2px solid rgba(0,79,152,0.07)', borderRadius: '2px', transform: 'rotate(15deg)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '100px', left: '-50px', width: '200px', height: '200px', border: '2px solid rgba(255,87,34,0.06)', borderRadius: '2px', transform: 'rotate(-10deg)', pointerEvents: 'none' }} />

      <Header />
      <main className="pt-[84px] px-4 sm:px-6 lg:px-8 pb-16 max-w-[720px] mx-auto" style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ paddingTop: '40px', marginBottom: '48px' }}>
          {isUpgradeFlow && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(245,158,11,0.1)', border: '2px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)', padding: '6px 14px', marginBottom: '16px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2.5">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F59E0B' }}>
                Oppgradering til Premium
              </span>
            </div>
          )}
          {!isUpgradeFlow && (
            <div style={{ display: 'inline-block', background: 'var(--color-accent)', padding: '4px 12px', marginBottom: '16px', borderRadius: 'var(--radius-sm)' }}>
              <span style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FFFFFF' }}>Fakturering</span>
            </div>
          )}
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '32px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '0.02em', color: 'var(--color-foreground)', marginBottom: '12px' }}>
            {isUpgradeFlow ? 'Betalingsinformasjon' : 'Fakturainformasjon'}
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '16px', fontWeight: 400, lineHeight: 1.6, color: 'var(--color-muted-foreground)' }}>
            {isUpgradeFlow
              ? 'Fyll inn faktureringsdetaljer for å fullføre oppgraderingen til Premium (499 kr/mnd).'
              : 'Fyll inn din fakturerings- og leveringsinformasjon for abonnementet ditt.'}
          </p>

          {isUpgradeFlow && (
            <div style={{ marginTop: '20px', padding: '16px', background: 'rgba(0,79,152,0.06)', border: '2px solid rgba(0,79,152,0.15)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" />
                <path d="M1 10h22" />
              </svg>
              <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: 'var(--color-muted-foreground)' }}>
                Faktura sendes til din e-postadresse. Ingen kredittkort nødvendig.
              </p>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {Object.keys(errors)?.length > 0 && (
            <div style={{ marginBottom: '32px', padding: '16px', borderLeft: '4px solid #EF4444', background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.2)', borderLeftWidth: '4px' }}>
              <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', fontWeight: 500, color: '#EF4444' }}>
                Vennligst fyll ut alle pakrevde felt korrekt.
              </p>
            </div>
          )}

          <div style={{ marginBottom: '48px', paddingBottom: '48px', borderBottom: '1px solid rgba(156, 163, 175, 0.15)' }}>
            <PersonalInfoSection formData={formData} errors={errors} onChange={handleChange} onBlur={handleBlur} />
          </div>

          <div style={{ marginBottom: '48px', paddingBottom: '48px', borderBottom: '1px solid rgba(156, 163, 175, 0.15)' }}>
            <AddressSection formData={formData} errors={errors} onChange={handleChange} onBlur={handleBlur} />
          </div>

          <div style={{ marginBottom: '48px' }}>
            <InvoiceDeliverySection formData={formData} onChange={handleChange} />
          </div>

          <div style={{ height: '2px', background: 'var(--color-border)', marginBottom: '32px' }} />

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }} className="payment-btn-row">
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                flex: '1',
                minWidth: '200px',
                background: isSubmitting ? 'rgba(255, 87, 34, 0.6)' : '#FF5722',
                border: '2px solid #FF5722',
                borderRadius: 'var(--radius-sm)',
                padding: '14px 24px',
                fontFamily: 'var(--font-heading)',
                fontSize: '16px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#FFFFFF',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 250ms ease-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.background = '#E64A19'; }}
              onMouseLeave={e => { if (!isSubmitting) e.currentTarget.style.background = '#FF5722'; }}
            >
              {isSubmitting ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  {isUpgradeFlow ? 'Behandler...' : 'Lagrer...'}
                </>
              ) : isUpgradeFlow ? 'Bekreft oppgradering' : 'Lagre fakturainformasjon'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/subscription-portal')}
              style={{
                background: 'transparent',
                border: '2px solid var(--color-primary)',
                borderRadius: 'var(--radius-sm)',
                padding: '14px 24px',
                fontFamily: 'var(--font-heading)',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--color-primary)',
                cursor: 'pointer',
                transition: 'all 250ms ease-out',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0, 79, 152, 0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              Avbryt
            </button>
          </div>
        </form>
      </main>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PaymentInvoiceForm;
